import type MockAdapter from 'axios-mock-adapter';
import type { PagedResult } from '@/types';
import { mockUsers, mockCredentials } from './data/users';
import { mockRoles } from './data/roles';
import { mockPermissions } from './data/permissions';
import { mockDashboards } from './data/dashboards';
import { mockNotifications } from './data/notifications';
import { mockAuditLogs } from './data/auditLogs';
import { mockReports } from './data/reports';
import { sampleDataSets } from './data/sampleDataSets';
import {
  generateActivity,
  generatePieData,
  generateTableRows,
  generateTimeSeries,
  generateScatterData,
  generateBubbleData,
  generateRadarData,
  generateBoxplotData,
  generateHeatmapData,
  generateCandlestickData,
  generateSankeyData,
  generateFunnelData,
  generateTreeData,
  generateTreemapData,
  generateSunburstData,
  generateGraphData,
  generateParallelData,
  generateCalendarData,
  generateThemeRiverData,
  generatePictorialBarData,
  generateNightingaleData,
  generateWaterfallData,
} from './data/widgetData';
import { newId } from '@/utils';
import type { Dashboard } from '@/types';

function paginate<T>(items: T[], page = 1, pageSize = 20): PagedResult<T> {
  const start = (page - 1) * pageSize;
  return { items: items.slice(start, start + pageSize), total: items.length, page, pageSize };
}

// Mutable in-memory stores
const store = {
  users: [...mockUsers],
  roles: [...mockRoles],
  dashboards: [...mockDashboards] as Dashboard[],
  notifications: [...mockNotifications],
  reports: [...mockReports] as any[],
  audit: [...mockAuditLogs],
};

export function registerMockHandlers(mock: MockAdapter): void {
  // ---- AUTH ----
  mock.onPost('/auth/login').reply((config) => {
    const body = JSON.parse(config.data ?? '{}');
    const email = body.email as string;
    const password = body.password as string;
    if (mockCredentials[email] !== password) {
      return [401, { code: 'INVALID_CREDENTIALS', message: 'Invalid email or password' }];
    }
    const user = store.users.find((u) => u.email === email)!;
    return [
      200,
      {
        user,
        accessToken: `mock_access_${user.id}_${Date.now()}`,
        refreshToken: `mock_refresh_${user.id}_${Date.now()}`,
        expiresIn: 3600,
      },
    ];
  });

  mock.onPost('/auth/logout').reply(200, { success: true });

  mock.onPost('/auth/refresh').reply((config) => {
    const body = JSON.parse(config.data ?? '{}');
    if (!body.refreshToken) return [401, { message: 'No refresh token' }];
    return [
      200,
      {
        accessToken: `mock_access_refreshed_${Date.now()}`,
        refreshToken: `mock_refresh_${Date.now()}`,
        expiresIn: 3600,
      },
    ];
  });

  mock.onGet('/auth/me').reply((config) => {
    const auth = (config.headers?.Authorization as string) ?? '';
    const userId = auth.match(/mock_access_(user_\d+)/)?.[1];
    const user = store.users.find((u) => u.id === userId) ?? store.users[0];
    return [200, user];
  });

  // ---- DASHBOARDS ----
  mock.onGet('/dashboards').reply((config) => {
    const { page = 1, pageSize = 20 } = config.params ?? {};
    return [200, paginate(store.dashboards, Number(page), Number(pageSize))];
  });

  mock.onGet(/\/dashboards\/[^/]+$/).reply((config) => {
    const id = config.url!.split('/').pop()!;
    const d = store.dashboards.find((x) => x.id === id);
    return d ? [200, d] : [404, { message: 'Not found' }];
  });

  mock.onPost('/dashboards').reply((config) => {
    const body = JSON.parse(config.data ?? '{}');
    const now = new Date().toISOString();
    const dash: Dashboard = {
      id: newId('dash'),
      name: body.name ?? 'New Dashboard',
      description: body.description,
      ownerId: 'user_1',
      version: 1,
      versions: [],
      widgets: [],
      layouts: { lg: [], md: [], sm: [], xs: [], xxs: [] },
      tags: body.tags,
      createdAt: now,
      updatedAt: now,
    };
    store.dashboards.push(dash);
    return [201, dash];
  });

  mock.onPut(/\/dashboards\/[^/]+$/).reply((config) => {
    const id = config.url!.split('/').pop()!;
    const idx = store.dashboards.findIndex((x) => x.id === id);
    if (idx < 0) return [404, { message: 'Not found' }];
    const patch = JSON.parse(config.data ?? '{}');
    const prev = store.dashboards[idx];
    const nextVersion = prev.version + 1;
    const versions = [
      ...(prev.versions ?? []),
      {
        version: prev.version,
        layouts: prev.layouts,
        widgets: prev.widgets,
        createdAt: prev.updatedAt ?? prev.createdAt,
      },
    ].slice(-20);
    store.dashboards[idx] = {
      ...prev,
      ...patch,
      version: nextVersion,
      versions,
      updatedAt: new Date().toISOString(),
    };
    return [200, store.dashboards[idx]];
  });

  mock.onDelete(/\/dashboards\/[^/]+$/).reply((config) => {
    const id = config.url!.split('/').pop()!;
    store.dashboards = store.dashboards.filter((x) => x.id !== id);
    return [204];
  });

  mock.onPost(/\/dashboards\/[^/]+\/clone$/).reply((config) => {
    const id = config.url!.split('/')[2];
    const src = store.dashboards.find((x) => x.id === id);
    if (!src) return [404, { message: 'Not found' }];
    const clone: Dashboard = {
      ...src,
      id: newId('dash'),
      name: `${src.name} (Copy)`,
      version: 1,
      versions: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    store.dashboards.push(clone);
    return [201, clone];
  });

  mock.onGet(/\/dashboards\/[^/]+\/versions$/).reply((config) => {
    const id = config.url!.split('/')[2];
    const d = store.dashboards.find((x) => x.id === id);
    return [200, d?.versions ?? []];
  });

  // ---- WIDGETS ----
  mock.onGet(/\/widgets\/[^/]+\/data$/).reply((config) => {
    const type = config.url!.split('/')[2];
    const params = (config.params ?? {}) as Record<string, unknown>;
    const dataSetId = params.dataSet as string | undefined;
    const ds = dataSetId ? sampleDataSets.find((d) => d.id === dataSetId) : undefined;

    if (ds) {
      const xCol = params.xColumn as string | undefined;
      const yCol = params.yColumn as string | undefined;
      const data = ds.rows;

      switch (type) {
        case 'line':
        case 'area':
        case 'bar': {
          if (xCol && yCol) {
            const map = new Map<string, number>();
            for (const row of data) {
              const key = String(row[xCol] ?? '');
              const val = Number(row[yCol]) || 0;
              map.set(key, (map.get(key) ?? 0) + val);
            }
            const categories = [...map.keys()];
            const values = categories.map((k) => map.get(k)!);
            return [200, { categories, series: [{ name: yCol, data: values }] }];
          }
          const s = generateTimeSeries();
          return [200, { categories: s.categories, series: [{ name: 'Value', data: s.values }] }];
        }
        case 'pie': {
          if (xCol && yCol) {
            const map = new Map<string, number>();
            for (const row of data) {
              const key = String(row[xCol] ?? '');
              const val = Number(row[yCol]) || 0;
              map.set(key, (map.get(key) ?? 0) + val);
            }
            return [200, [...map.entries()].map(([name, value]) => ({ name, value }))];
          }
          return [200, generatePieData()];
        }
        case 'table':
          return [200, data];
        case 'gauge': {
          if (yCol) {
            const total = data.reduce((s, r) => s + (Number(r[yCol]) || 0), 0);
            return [200, { value: total, max: Math.round(total * 1.25) }];
          }
          return [200, { value: Math.round(Math.random() * 100), max: 100 }];
        }
        case 'activity':
          return [
            200,
            data.map((r, i) => ({
              id: `act_${i}`,
              user: String(Object.values(r)[0] ?? `User ${i}`),
              text: `updated ${String(Object.keys(r)[1] ?? 'record')}: ${String(Object.values(r)[1] ?? '')}`,
              time: new Date(Date.now() - i * 3_600_000).toISOString(),
            })),
          ];
        case 'kpi':
          if (yCol) {
            const total = data.reduce((s, r) => s + (Number(r[yCol]) || 0), 0);
            return [200, { value: total, delta: 0 }];
          }
          return [
            200,
            { value: Math.round(Math.random() * 100000), delta: (Math.random() - 0.5) * 20 },
          ];
        case 'scatter': {
          if (xCol && yCol) {
            const points = data.map((r, i) => [Number(r[xCol]) || i, Number(r[yCol]) || 0] as [number, number]);
            return [200, points];
          }
          return [200, generateScatterData()];
        }
        case 'bubble':
          if (xCol && yCol) {
            const bubbles = data.map((r, i) => ({ x: Number(r[xCol]) || i, y: Number(r[yCol]) || 0, size: Math.random() * 100 }));
            return [200, { data: bubbles }];
          }
          return [200, generateBubbleData()];
        case 'radar':
          return [200, generateRadarData()];
        case 'boxplot':
          return [200, generateBoxplotData()];
        case 'heatmap':
          return [200, generateHeatmapData()];
        case 'candlestick':
          return [200, generateCandlestickData()];
        case 'sankey':
          return [200, generateSankeyData()];
        case 'funnel':
          return [200, generateFunnelData()];
        case 'tree':
          return [200, generateTreeData()];
        case 'treemap':
          return [200, generateTreemapData()];
        case 'sunburst':
          return [200, generateSunburstData()];
        case 'graph':
          return [200, generateGraphData()];
        case 'parallel':
          return [200, generateParallelData()];
        case 'calendar':
          return [200, generateCalendarData()];
        case 'theme-river':
          return [200, generateThemeRiverData()];
        case 'pictorial-bar':
          return [200, generatePictorialBarData()];
        case 'nightingale':
          return [200, generateNightingaleData()];
        case 'waterfall':
          return [200, generateWaterfallData()];
        default:
          return [200, {}];
      }
    }

    switch (type) {
      case 'line':
      case 'area': {
        const s = generateTimeSeries();
        return [200, { categories: s.categories, series: [{ name: 'Value', data: s.values }] }];
      }
      case 'bar': {
        const s = generateTimeSeries(8, 200, 100);
        return [200, { categories: s.categories, series: [{ name: 'Value', data: s.values }] }];
      }
      case 'pie':
        return [200, generatePieData()];
      case 'table':
        return [200, generateTableRows()];
      case 'activity':
        return [200, generateActivity()];
      case 'gauge':
        return [200, { value: Math.round(Math.random() * 100), max: 100 }];
      case 'kpi':
        return [
          200,
          { value: Math.round(Math.random() * 100000), delta: (Math.random() - 0.5) * 20 },
        ];
      case 'scatter':
        return [200, generateScatterData()];
      case 'bubble':
        return [200, generateBubbleData()];
      case 'radar':
        return [200, generateRadarData()];
      case 'boxplot':
        return [200, generateBoxplotData()];
      case 'heatmap':
        return [200, generateHeatmapData()];
      case 'candlestick':
        return [200, generateCandlestickData()];
      case 'sankey':
        return [200, generateSankeyData()];
      case 'funnel':
        return [200, generateFunnelData()];
      case 'tree':
        return [200, generateTreeData()];
      case 'treemap':
        return [200, generateTreemapData()];
      case 'sunburst':
        return [200, generateSunburstData()];
      case 'graph':
        return [200, generateGraphData()];
      case 'parallel':
        return [200, generateParallelData()];
      case 'calendar':
        return [200, generateCalendarData()];
      case 'theme-river':
        return [200, generateThemeRiverData()];
      case 'pictorial-bar':
        return [200, generatePictorialBarData()];
      case 'nightingale':
        return [200, generateNightingaleData()];
      case 'waterfall':
        return [200, generateWaterfallData()];
      default:
        return [200, {}];
    }
  });

  // ---- USERS ----
  mock.onGet('/users').reply((config) => {
    const { page = 1, pageSize = 20 } = config.params ?? {};
    return [200, paginate(store.users, Number(page), Number(pageSize))];
  });
  mock.onGet(/\/users\/[^/]+$/).reply((config) => {
    const id = config.url!.split('/').pop();
    const u = store.users.find((x) => x.id === id);
    return u ? [200, u] : [404, { message: 'Not found' }];
  });
  mock.onPost('/users').reply((config) => {
    const body = JSON.parse(config.data ?? '{}');
    const u = {
      id: newId('user'),
      createdAt: new Date().toISOString(),
      isActive: true,
      permissions: [],
      roles: [],
      ...body,
    };
    store.users.push(u);
    return [201, u];
  });
  mock.onPut(/\/users\/[^/]+$/).reply((config) => {
    const id = config.url!.split('/').pop();
    const idx = store.users.findIndex((x) => x.id === id);
    if (idx < 0) return [404, { message: 'Not found' }];
    store.users[idx] = { ...store.users[idx], ...JSON.parse(config.data ?? '{}') };
    return [200, store.users[idx]];
  });
  mock.onDelete(/\/users\/[^/]+$/).reply((config) => {
    const id = config.url!.split('/').pop();
    store.users = store.users.filter((x) => x.id !== id);
    return [204];
  });

  // ---- ROLES / PERMISSIONS ----
  mock.onGet('/roles').reply(200, store.roles);
  mock.onPut(/\/roles\/[^/]+$/).reply((config) => {
    const id = config.url!.split('/').pop();
    const idx = store.roles.findIndex((x) => x.id === id);
    if (idx < 0) return [404, { message: 'Not found' }];
    store.roles[idx] = { ...store.roles[idx], ...JSON.parse(config.data ?? '{}') };
    return [200, store.roles[idx]];
  });
  mock.onGet('/permissions').reply(200, mockPermissions);

  // ---- NOTIFICATIONS ----
  mock.onGet('/notifications').reply(200, paginate(store.notifications, 1, 50));
  mock.onPost(/\/notifications\/[^/]+\/read$/).reply((config) => {
    const id = config.url!.split('/')[2];
    const n = store.notifications.find((x) => x.id === id);
    if (n) n.read = true;
    return [200, { success: true }];
  });
  mock.onPost('/notifications/read-all').reply(() => {
    store.notifications.forEach((n) => (n.read = true));
    return [200, { success: true }];
  });

  // ---- REPORTS ----
  mock.onGet('/reports').reply((config) => {
    const { page = 1, pageSize = 20 } = config.params ?? {};
    return [200, paginate(store.reports, Number(page), Number(pageSize))];
  });
  mock.onPost('/reports').reply((config) => {
    const body = JSON.parse(config.data ?? '{}');
    const r = {
      id: newId('rep'),
      status: 'pending',
      createdAt: new Date().toISOString(),
      recipients: [],
      ...body,
    };
    store.reports.push(r);
    return [201, r];
  });
  mock.onPost(/\/reports\/[^/]+\/run$/).reply((config) => {
    const id = config.url!.split('/')[2];
    const r = store.reports.find((x) => x.id === id);
    if (!r) return [404, { message: 'Not found' }];
    r.status = 'completed';
    r.lastRunAt = new Date().toISOString();
    return [200, r];
  });
  mock.onGet(/\/reports\/[^/]+\/download$/).reply((config) => {
    const id = config.url!.split('/')[2];
    const r = store.reports.find((x) => x.id === id);
    if (!r) return [404, { message: 'Not found' }];
    const rows = [
      ['Month', 'Revenue', 'Orders', 'Conversion'],
      ['Jan', '45200', '384', '3.2%'],
      ['Feb', '48900', '412', '3.5%'],
      ['Mar', '52300', '438', '3.8%'],
      ['Apr', '49800', '415', '3.4%'],
      ['May', '56100', '467', '4.1%'],
      ['Jun', '61200', '502', '4.3%'],
    ];
    const csv = rows.map((r) => r.join(',')).join('\n');
    return [
      200,
      csv,
      {
        'Content-Type': r.format === 'csv' ? 'text/csv' : 'application/octet-stream',
        'Content-Disposition': `attachment; filename="${r.name.replace(/\s+/g, '_')}.${r.format}"`,
      },
    ];
  });

  mock.onDelete(/\/reports\/[^/]+$/).reply((config) => {
    const id = config.url!.split('/').pop();
    store.reports = store.reports.filter((x) => x.id !== id);
    return [204];
  });

  // ---- AUDIT ----
  mock.onGet('/audit-logs').reply((config) => {
    const { page = 1, pageSize = 20 } = config.params ?? {};
    return [200, paginate(store.audit, Number(page), Number(pageSize))];
  });

  // Fallback: passthrough any unhandled to 404 for visibility
  mock.onAny().passThrough();
}
